import type { z } from 'zod';

import type { TemplateName } from '../core/assetType.js';
import type { Locator, Severity, ValidationIssue } from '../core/issues.js';

import { toPointer } from '../core/issues.js';

/**
 * One activity type, as a module that can be called.
 *
 * The validator already owns cross-cutting rules that sweep a whole document
 * (`scoreRoutes`, `richEndingShape`). This is the other axis: everything true of a
 * single* activity of one type, in one place, so a caller holding one activity and
 * nothing else can ask whether it is well formed. The Claude connector is that
 * caller — it has a model's JSON response and no document to put it in yet.
 *
 * `setup` is the shape; `check` is everything the shape cannot say, which for these
 * activities is mostly agreement between fields — a correct answer that names an
 * option nobody wrote is well-shaped and still broken.
 */
export interface ActivityCheckContext {
  /** Path from the caller's root to the activity object, for issue paths. */
  path: (string | number)[];
  locator: Locator;
  /**
   * The outcome ids the writer defined on the node (its exit-route keys). Present
   * when the caller knows them; an activity routing to an outcome outside this set
   * has invented a branch the story cannot follow.
   */
  outcomeIds?: string[];
  /**
   * The template the activity's document is validated as, when the caller knows it. A
   * few setup fields are read differently per template — see `templatePolicy.ts`.
   */
  templateName?: TemplateName;
}

export interface ActivityDefinition {
  /** The `activity.type` discriminator this module owns. */
  type: string;
  /**
   * One line naming what the activity is, in the words a brief uses. Read into the
   * producer prompt so the catalogue a model chooses from is generated from the same
   * modules that judge its answer, and cannot drift from them.
   */
  summary: string;
  /**
   * False for a type a person has to author: its setup needs something only a person can pick,
   * such as a picture from the content assets, so the producer is never offered it.
   */
  producible?: boolean;
  /** Zod shape for this type's `setup`. Loose: unknown product fields ride through. */
  setup: z.ZodType;
  /** Cross-field integrity this type requires. Runs only when `setup` parses. */
  check?: (setup: Record<string, unknown>, ctx: ActivityCheckContext) => ValidationIssue[];
}

/** Issue factory for an activity module: one `constraint.detail` per activity type. */
export const activityIssue = (type: string) =>
  (
    code: string,
    message: string,
    path: (string | number)[],
    ctx: ActivityCheckContext,
    currentValue?: unknown,
    severity: Severity = 'error',
  ): ValidationIssue => ({
    code: `activity.${type}.${code}`,
    severity,
    message,
    path,
    pointer: toPointer(path),
    locator: ctx.locator,
    currentValue,
    constraint: { kind: 'custom', detail: `${type} setup integrity` },
  });

export const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};

export const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
